import { SetStateAction, useCallback } from 'react';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown';
import type { SavedFiltersProps } from 'talent_matching/components/filters/saved_filters';
import type { GreenhouseFilterPreset } from 'talent_matching/talent_rediscovery/types';
import { setDefaultSavedSearch } from 'talent_matching/api';
import { useUserContext } from 'talent_matching/contexts/user_context';
import type { TalentMatchingSource } from 'talent_matching/lib/tracker';

type UseSavedFiltersConfig = {
  featureType: string;
  formDefaults: Record<string, unknown>;
  savedFilterSet: Item | null;
  setSavedFilterSet: (item: Item | null) => void;
  savedSearchOptions: Item[];
  setSavedSearchOptions: (options: SetStateAction<Item[]>) => void;
  parseSavedSearch: (response: unknown) => Record<string, unknown>;
  buildQueryString: () => string;
  greenhouseFilterPresets?: GreenhouseFilterPreset[];
  rememberLastUsed?: boolean;
  trackingSource?: TalentMatchingSource;
};

type UseSavedFiltersReturn = {
  savedFiltersProps: SavedFiltersProps;
  handleClearAll: () => void;
};

export function useSavedFilters(
  config: UseSavedFiltersConfig
): UseSavedFiltersReturn {
  const { reset } = useFormContext();
  const {
    hiringPlanId,
    paths: { defaultSavedSearchPath },
  } = useUserContext();
  const {
    featureType,
    formDefaults,
    savedFilterSet,
    setSavedFilterSet,
    savedSearchOptions,
    setSavedSearchOptions,
    parseSavedSearch,
    buildQueryString,
    greenhouseFilterPresets,
    rememberLastUsed = false,
    trackingSource,
  } = config;

  const handleClearAll = useCallback(() => {
    setSavedFilterSet(null);
    reset(formDefaults);
    if (rememberLastUsed) {
      // Persist the "empty" preference (both targets null) so next visit
      // starts empty instead of auto-applying the suggested filter.
      setDefaultSavedSearch(defaultSavedSearchPath, {
        hiringPlanId,
        featureType,
        savedSearchId: null,
        greenhouseFilterKey: null,
      });
    }
  }, [
    setSavedFilterSet,
    reset,
    formDefaults,
    defaultSavedSearchPath,
    hiringPlanId,
    featureType,
    rememberLastUsed,
  ]);

  return {
    savedFiltersProps: {
      featureType,
      savedFilterSet,
      setSavedFilterSet,
      setSavedSearchOptions,
      formDefaults,
      savedSearchOptions,
      parseSavedSearch,
      buildQueryString,
      greenhouseFilterPresets,
      rememberLastUsed,
      trackingSource,
    },
    handleClearAll,
  };
}
